import { useCallback, useMemo } from 'react';
import { Activity } from '@shared/models/design/activities/activity.model';
import { ItemOrItemId, SemiPartial } from '@shared/models/type-helpers.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import { mutateDesign } from '@shared/helpers/design/mutators';
import { useBuildingDesign, useUpdateBuildingDesign } from './design.hooks';
import { ActivityMix } from '@shared/models/design/activities/activity-type.model';
import {
  getActivityMixFromDesign,
  getOtherActivityAreaFromActivityMix,
  getProgramActivityAreaFromActivityMix,
  getProgramActivityMixFromActivityMix,
} from '@shared/helpers/design/activity-mix.helpers';

type AddActivityFn = (
  base: Parameters<typeof mutateDesign.activity.add>[1],
) => Promise<void>;
type RemoveActivityFn = (activityOrId: ItemOrItemId<Activity>) => Promise<void>;
type UpdateActivityFn = (update: SemiPartial<Activity, 'id'>) => Promise<void>;

/**
 * Get the building design activities. TODO: Rename when old activities are removed.
 */
export const useDesignActivities = (): Activity[] => {
  return useBuildingDesign().activities;
};

/**
 * Get a map of activities by id.
 * @returns Map of activities by id.
 */
export const useActivityByIdMap = (): Map<Activity['id'], Activity> => {
  const activities = useDesignActivities();
  return useMemo(
    () => new Map(activities.map((activity) => [activity.id, activity])),
    [activities],
  );
};

/**
 * Get a map of activities by type. Currently only one activity per type is allowed.
 * @returns Map of activities by type.
 */
export const useActivityByTypeMap = (): Map<Activity['type'], Activity> => {
  const activities = useDesignActivities();
  return useMemo(
    () => new Map(activities.map((activity) => [activity.type, activity])),
    [activities],
  );
};

/**
 * Add a new activity to the building design
 */
export const useAddActivity = (): AddActivityFn => {
  const updateBuildingDesign = useUpdateBuildingDesign();

  return useCallback<AddActivityFn>(
    async (base) => {
      await updateBuildingDesign((design) => {
        const nextDesign = mutateDesign.activity.add(design, base);
        if (guards.is.error(nextDesign)) {
          console.error(nextDesign);
        }
        return nextDesign;
      });
    },
    [updateBuildingDesign],
  );
};

/**
 * Update an activity in the building design
 */
export const useUpdateActivity = (): UpdateActivityFn => {
  const updateBuildingDesign = useUpdateBuildingDesign();

  return useCallback<UpdateActivityFn>(
    async (update) => {
      await updateBuildingDesign((design) =>
        mutateDesign.activity.update(design, update),
      );
    },
    [updateBuildingDesign],
  );
};

/**
 * Remove an activity and its storey references from the design
 */
export const useRemoveActivity = (): RemoveActivityFn => {
  const updateBuildingDesign = useUpdateBuildingDesign();

  return useCallback<RemoveActivityFn>(
    async (activityOrId) => {
      await updateBuildingDesign((design) =>
        mutateDesign.activity.remove(design, activityOrId),
      );
    },
    [updateBuildingDesign],
  );
};

/**
 * Get the activity mix from the building design.
 * @returns Activity mix. How much m2 of each activity type is in the building.
 */
export const useActivityMix = (): ActivityMix => {
  const design = useBuildingDesign();
  return useMemo(() => getActivityMixFromDesign(design), [design]);
};

/**
 * Get the program activity mix from the building design.
 * @returns Program activity mix. How much m2 of each program activity type is in the building.
 */
export const useProgramActivityMix = (
  includeUnset: boolean = false,
): ActivityMix => {
  const activityMix = useActivityMix();
  return useMemo(
    () => getProgramActivityMixFromActivityMix(activityMix, includeUnset),
    [activityMix, includeUnset],
  );
};

/**
 * Get the program activity area from the building design.
 * @returns Program activity area. How much m2 of the program activities are in the building.
 */
export const useProgramActivityArea = (
  includeUnset: boolean = false,
): number => {
  const programActivityMix = useProgramActivityMix(includeUnset);
  return getProgramActivityAreaFromActivityMix(
    programActivityMix,
    includeUnset,
  );
};

export const useDesignNia = (): number => useProgramActivityArea(true);

export const useOtherActivityArea = (): number => {
  const activityMix = useActivityMix();
  return getOtherActivityAreaFromActivityMix(activityMix);
};
